import { normalizeRanges } from "./availability";

/**
 * Un mentor n'est RÉELLEMENT réservable que si les trois étapes invisibles sont
 * faites. Elles ne figurent nulle part dans son profil public, d'où des mentors
 * listés vers qui personne ne peut réserver :
 *
 *  - `isOnboarded` : Calendar.js saute purement et simplement chaque jour d'un
 *    mentor non onboardé (components/ui/Calendar/Calendar.js). Sans ça, aucun
 *    créneau ne peut apparaître, quels que soient les horaires enregistrés.
 *  - `stripeAccountStatus === "active"` : sans compte Stripe actif, le mentor ne
 *    peut pas être payé.
 *  - au moins une plage horaire dans la semaine : sinon le calendrier est vide
 *    par construction.
 *
 * `mentorHasOpenAvailabilities` reste une déclaration du mentor (une case qu'il
 * coche dans son espace) : nécessaire, mais jamais suffisante.
 *
 * Remplir ces conditions ne publie plus le mentor : son profil part en
 * validation auprès de l'équipe, et seul un profil validé (`mentorReviewStatus
 * === "approved"`) est listé et réservable. Miroir PHP de la règle :
 * believemy_v3/src/Service/MentorReviewService.php (isReadyForReview), à
 * modifier ensemble.
 */

/** En dessous de ce nombre de mentors réservables, l'annuaire n'est pas affiché. */
export const MENTORING_DIRECTORY_MINIMUM = 3;

/** Statuts de validation du profil par l'équipe (null = jamais soumis). */
export const MENTOR_REVIEW_PENDING = "pending";
export const MENTOR_REVIEW_APPROVED = "approved";
export const MENTOR_REVIEW_REJECTED = "rejected";
/** Validé, mais hors du site jusqu'à la mise en ligne groupée du lancement. */
export const MENTOR_REVIEW_SCHEDULED = "scheduled";

/**
 * Points qu'un refus peut signaler. Mêmes codes que
 * MentorReviewService::REASONS ; libellés dans mentor_review_reason_*.
 */
export const MENTOR_REVIEW_REASONS = ["photo", "title", "presentation", "pseudo"];

const WEEKDAY_FIELDS = [
    "mentorMonday",
    "mentorTuesday",
    "mentorWednesday",
    "mentorThursday",
    "mentorFriday",
    "mentorSaturday",
    "mentorSunday",
];

/** Toutes les conditions de réservation sont remplies : le profil peut être relu. */
export function isMentorReadyForReview(mentor) {
    if (!mentor) return false;
    if (!mentor.mentorPseudo || !mentor.mentorPicture) return false;
    if (!mentor.mentorHasOpenAvailabilities) return false;
    if (!mentor.isOnboarded) return false;
    if (mentor.stripeAccountStatus !== "active") return false;

    return WEEKDAY_FIELDS.some(
        (day) => normalizeRanges(mentor[day]).length > 0,
    );
}

export function isMentorBookable(mentor) {
    if (!mentor) return false;
    if (mentor.mentorReviewStatus !== MENTOR_REVIEW_APPROVED) return false;

    return isMentorReadyForReview(mentor);
}

export function filterBookableMentors(mentors) {
    return (mentors || []).filter(isMentorBookable);
}
